/**
 * Select a Launch Queue.
 */
import {
  GREEN_550,
  GREEN_600,
  hexToRGB,
  RED_450,
  RED_550,
} from '@wandb/weave/common/css/globals.styles';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, IconMagicWandStick} from '@wandb/weave/components/Icon';
import React from 'react';
import {FormatOptionLabelMeta} from 'react-select';
import styled from 'styled-components';

import {Tooltip} from '../Tooltip';
import {ResourceChip} from './ResourceChip';
import * as S from './SelectComponents.styles';
import {isGlobalQueue} from './utils';
import {JsonObject, ResourceType} from './utilTypes';

type CircleProps = {
  isActive: boolean;
};

export const Circle = styled.div<CircleProps>`
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background-color: ${props =>
    props.isActive ? hexToRGB(GREEN_550, 0.15) : hexToRGB(RED_450, 0.16)};
  color: ${props => (props.isActive ? GREEN_600 : RED_550)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

export type QueueOptions = QueueOption | GroupedQueueOptions;

export type QueueOption = {
  isActive: boolean;
  value: string;
  entity: string;
  project: string;
  queue: string;
  queueID: string;
  defaultResourceConfigID: string;
  resourceType: ResourceType;
  config: JsonObject;
  disabled: boolean;
};

// This option represents creating a new Starter queue
export const CREATE_QUEUE_PLACEHOLDER: QueueOption = {
  isActive: true,
  value: '',
  entity: '',
  project: '',
  queue: '',
  queueID: '',
  defaultResourceConfigID: '',
  resourceType: 'local-container',
  config: {},
  disabled: false,
};

export type GroupedQueueOptions = {
  label: string;
  options: QueueOption[];
};

type SelectQueueProps = {
  options: QueueOptions[];
  defaultQueueOption?: QueueOption;
  onSelectQueue: (option: QueueOption) => void;
};

const OptionLabel = (
  props: QueueOption,
  meta: FormatOptionLabelMeta<QueueOption>
) => {
  const {isActive, entity, queue, resourceType} = props;
  if (queue === '') {
    return (
      <S.SelectOptionLabel>
        <IconMagicWandStick style={{marginRight: 8}} />
        <S.SelectOptionLabel>Create a 'Starter' queue</S.SelectOptionLabel>
        <ResourceChip value="local-container" />
      </S.SelectOptionLabel>
    );
  }

  const isValue = meta.context === 'value'; // selected value, not menu
  const isGlobal = isGlobalQueue(entity, queue);
  const label = isValue && !isGlobal ? `${entity}/${queue}` : queue;
  const icon = isActive ? 'checkmark-circle' : 'warning';
  const tooltip = isGlobal
    ? 'This queue is used for tutorial purposes and only accepts the “OpenAI Evals” public job'
    : isActive
    ? 'Queue has at least one running agent'
    : 'You can add items to this queue but it has no running agents';

  if (!isGlobal) {
    return (
      <S.SelectOption>
        <Tooltip
          content={tooltip}
          trigger={
            <Circle isActive={isActive}>
              <Icon name={icon} width={14} height={14} />
            </Circle>
          }
        />
        <S.SelectOptionLabel>{label}</S.SelectOptionLabel>
        <ResourceChip value={resourceType} />
      </S.SelectOption>
    );
  }

  return (
    <S.SelectOption>
      <Circle isActive={isActive}>
        <Icon name={icon} width={14} height={14} />
      </Circle>
      <Tooltip
        content={tooltip}
        trigger={<S.SelectOptionLabel>{label}</S.SelectOptionLabel>}
      />
      <ResourceChip value={resourceType} />
    </S.SelectOption>
  );
};

export const SelectQueue = ({
  options,
  defaultQueueOption,
  onSelectQueue,
}: SelectQueueProps) => {
  const onChange = (option: QueueOption | null) => {
    if (option) {
      onSelectQueue(option);
    }
  };

  return (
    <Select<QueueOption, false, GroupedQueueOptions>
      isOptionDisabled={q => q.disabled}
      options={options}
      defaultValue={defaultQueueOption}
      onChange={onChange}
      formatOptionLabel={OptionLabel}
      placeholder="Select a Launch queue"
      noOptionsMessage={({inputValue}) => {
        return !inputValue ? 'No queue available' : 'No results found';
      }}
    />
  );
};
